import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { playNewOrderSound } from '../../../utils/notificationSound';
import './Orders.css';

const ORDERS_POLL_INTERVAL_MS = 30000;

const Orders = () => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const previousOrderIdsRef = useRef(new Set());

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/delivery-driver/orders`);
      const newOrders = response.data || [];
      const newIds = new Set(newOrders.map((o) => o._id));
      if (previousOrderIdsRef.current.size > 0 && document.visibilityState === 'visible') {
        const hasNew = newOrders.some((o) => !previousOrderIdsRef.current.has(o._id));
        if (hasNew) playNewOrderSound();
      }
      previousOrderIdsRef.current = newIds;
      setOrders(newOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, ORDERS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/delivery-driver/orders/${orderId}/status`, {
        status: newStatus
      });
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      showError(t('admin.statusUpdateFailed'));
    }
  };

  const filteredOrders = statusFilter === 'all'
    ? orders
    : orders.filter(order => order.status === statusFilter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#fff3cd', color: '#856404' },
      confirmed: { bg: '#d1ecf1', color: '#0c5460' },
      preparing: { bg: '#d4edda', color: '#155724' },
      readyForPickup: { bg: '#cce5ff', color: '#004085' },
      outForDelivery: { bg: '#cce5ff', color: '#004085' },
      delivered: { bg: '#d4edda', color: '#155724' },
      cancelled: { bg: '#f8d7da', color: '#721c24' }
    };
    const colors = statusColors[status] || { bg: '#e2e3e5', color: '#383d41' };
    return (
      <span
        className="status-badge"
        style={{
          backgroundColor: colors.bg,
          color: colors.color,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="loading-spinner">{t('admin.loading')}</div>;
  }

  return (
    <div className="driver-orders">
      <h2>{t('admin.myOrders')}</h2>

      <div className="orders-filters">
        <label>{t('admin.filterByStatus')}</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{t('admin.all')} {t('admin.orders')}</option>
          <option value="pending">{t('admin.pending')}</option>
          <option value="confirmed">{t('admin.confirmed')}</option>
          <option value="preparing">{t('admin.preparing')}</option>
          <option value="readyForPickup">{t('admin.readyForPickup') || 'Ready for pickup'}</option>
          <option value="outForDelivery">{t('admin.outForDelivery')}</option>
          <option value="delivered">{t('admin.delivered')}</option>
          <option value="cancelled">{t('admin.cancelled')}</option>
        </select>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <p>{t('admin.noOrders')}</p>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div>
                  <h3>{order.restaurantName}</h3>
                  <p className="order-number">{t('admin.orderNumber')} {order.orderNumber}</p>
                  <p className="order-date">{formatDate(order.createdAt)}</p>
                </div>
                {getStatusBadge(order.status)}
              </div>

              <div className="order-items">
                {order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div>
                      <span>{item.quantity}x {item.name}</span>
                      {item.selectedIngredients && item.selectedIngredients.length > 0 && (
                        <div className="order-item-ingredients">
                          {t('admin.ingredientsIncluded') || 'Ingredients'}: {item.selectedIngredients.join(', ')}
                        </div>
                      )}
                    </div>
                    <span>€{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="order-totals">
                <div className="total-row">
                  <span>{t('admin.total')}:</span>
                  <span>€{order.total.toFixed(2)}</span>
                </div>
              </div>

              {order.deliveryAddress && (
                <div className="delivery-address">
                  <strong>{t('admin.deliveryAddress')}:</strong>
                  <p>{order.deliveryAddress.street}, {order.deliveryAddress.city} {order.deliveryAddress.zipCode}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}</p>
                </div>
              )}

              <div className="customer-info">
                <strong>{t('admin.customer')}:</strong>
                <p>{order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}</p>
                {(order.customerPhone || order.user?.phoneNumber) && <p>{order.customerPhone ?? order.user?.phoneNumber}</p>}
              </div>

              {(order.driverInstructions || order.customerInstructions) && (
                <div className="driver-instructions">
                  <strong>{t('admin.driverInstructions')}:</strong>
                  <p>{order.driverInstructions || order.customerInstructions}</p>
                </div>
              )}

              <div className="order-actions">
                {order.status === 'readyForPickup' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleStatusChange(order._id, 'outForDelivery')}
                  >
                    {t('admin.pickUpOrder') || 'Pick up order'}
                  </button>
                )}
                {order.status === 'outForDelivery' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleStatusChange(order._id, 'delivered')}
                  >
                    {t('admin.markAsDelivered')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;
